'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["variant", "slots", "slotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useChat } from "../hooks/useChat.js";
import { useChatStore } from "../hooks/useChatStore.js";
import { useConversations } from "../hooks/useConversation.js";
import { markChatLayoutPane } from "../chat/internals/chatLayoutPaneKind.js";
import { ScrollRoot, ScrollViewport, ScrollScrollbar, ScrollThumb, scrollbarStyle, thumbStyle } from "../internals/ScrollAreaSlots.js";
import { mergeReactProps } from "../internals/mergeReactProps.js";
import { ConversationListItem } from "./ConversationListItem.js";
import { ConversationListItemAvatar } from "./ConversationListItemAvatar.js";
import { ConversationListItemContent } from "./ConversationListItemContent.js";
import { ConversationListTitle } from "./ConversationListTitle.js";
import { ConversationListPreview } from "./ConversationListPreview.js";
import { ConversationListTimestamp } from "./ConversationListTimestamp.js";
import { ConversationListUnreadBadge } from "./ConversationListUnreadBadge.js";
import { ConversationListItemActions } from "./ConversationListItemActions.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const lastFocusedConversationIdByStore = new WeakMap();
function ConversationListRenderedItem(props) {
  const {
    conversation,
    focused,
    selected,
    unread,
    variant,
    slots,
    slotProps,
    registerItemRef,
    onFocus,
    onSelect,
    onKeyDown
  } = props;
  const ownerState = {
    conversation,
    focused,
    selected,
    unread,
    variant
  };
  const Item = slots?.item ?? ConversationListItem;
  const ItemAvatar = slots?.itemAvatar ?? ConversationListItemAvatar;
  const ItemContent = slots?.itemContent ?? ConversationListItemContent;
  const Title = slots?.title ?? ConversationListTitle;
  const Preview = slots?.preview ?? ConversationListPreview;
  const Timestamp = slots?.timestamp ?? ConversationListTimestamp;
  const UnreadBadge = slots?.unreadBadge ?? ConversationListUnreadBadge;
  const ItemActions = slots?.itemActions ?? ConversationListItemActions;
  const itemSlotProps = useSlotProps({
    elementType: Item,
    externalSlotProps: slotProps?.item,
    ownerState,
    additionalProps: {
      conversation,
      selected,
      unread,
      focused,
      role: 'option',
      tabIndex: focused ? 0 : -1,
      'aria-selected': selected,
      ref: element => {
        registerItemRef(conversation.id, element);
      }
    }
  });
  const itemProps = mergeReactProps(itemSlotProps, {
    onClick: () => {
      onSelect(conversation.id);
    },
    onFocus: () => {
      onFocus(conversation.id);
    },
    onKeyDown: event => {
      onKeyDown(event, conversation.id);
    }
  });
  const itemAvatarProps = useSlotProps({
    elementType: ItemAvatar,
    externalSlotProps: slotProps?.itemAvatar,
    ownerState,
    additionalProps: {
      conversation,
      selected,
      unread,
      focused
    }
  });
  const itemContentProps = useSlotProps({
    elementType: ItemContent,
    externalSlotProps: slotProps?.itemContent,
    ownerState,
    additionalProps: {
      conversation,
      selected,
      unread,
      focused
    }
  });
  const titleProps = useSlotProps({
    elementType: Title,
    externalSlotProps: slotProps?.title,
    ownerState,
    additionalProps: {
      conversation,
      selected,
      unread,
      focused
    }
  });
  const previewProps = useSlotProps({
    elementType: Preview,
    externalSlotProps: slotProps?.preview,
    ownerState,
    additionalProps: {
      conversation,
      selected,
      unread,
      focused
    }
  });
  const timestampProps = useSlotProps({
    elementType: Timestamp,
    externalSlotProps: slotProps?.timestamp,
    ownerState,
    additionalProps: {
      conversation,
      selected,
      unread,
      focused
    }
  });
  const unreadBadgeProps = useSlotProps({
    elementType: UnreadBadge,
    externalSlotProps: slotProps?.unreadBadge,
    ownerState,
    additionalProps: {
      conversation,
      selected,
      unread,
      focused
    }
  });
  const itemActionsProps = useSlotProps({
    elementType: ItemActions,
    externalSlotProps: slotProps?.itemActions,
    ownerState,
    additionalProps: {
      conversation,
      selected,
      unread,
      focused
    }
  });
  if (variant === 'compact') {
    return /*#__PURE__*/_jsxs(Item, _extends({}, itemProps, {
      children: [/*#__PURE__*/_jsx(UnreadBadge, _extends({}, unreadBadgeProps)), /*#__PURE__*/_jsx(ItemContent, _extends({}, itemContentProps, {
        children: /*#__PURE__*/_jsx(Title, _extends({}, titleProps))
      })), /*#__PURE__*/_jsx(ItemActions, _extends({}, itemActionsProps))]
    }));
  }
  return /*#__PURE__*/_jsxs(Item, _extends({}, itemProps, {
    children: [/*#__PURE__*/_jsx(ItemAvatar, _extends({}, itemAvatarProps)), /*#__PURE__*/_jsxs(ItemContent, _extends({}, itemContentProps, {
      children: [/*#__PURE__*/_jsx(Title, _extends({}, titleProps)), /*#__PURE__*/_jsx(Preview, _extends({}, previewProps))]
    })), /*#__PURE__*/_jsx(Timestamp, _extends({}, timestampProps)), /*#__PURE__*/_jsx(UnreadBadge, _extends({}, unreadBadgeProps))]
  }));
}
function getInitialFocusedConversationId(conversations, activeConversationId, storedFocusedConversationId) {
  const hasConversation = id => id != null && conversations.some(conversation => conversation.id === id);
  if (hasConversation(storedFocusedConversationId)) {
    return storedFocusedConversationId;
  }
  if (hasConversation(activeConversationId)) {
    return activeConversationId;
  }
  return conversations[0]?.id;
}

// ---------------------------------------------------------------------------
// Functional default styles for the scroll slots.
// ---------------------------------------------------------------------------

const clScrollRootStyle = {
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
};
const clScrollViewportStyle = {
  overscrollBehavior: 'contain'
};
export const ConversationListRoot = markChatLayoutPane(/*#__PURE__*/React.forwardRef(function ConversationListRoot(props, ref) {
  const {
      variant = 'default',
      slots,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const conversations = useConversations();
  const {
    activeConversationId,
    setActiveConversation
  } = useChat();
  const store = useChatStore();
  const storedFocusedConversationId = lastFocusedConversationIdByStore.get(store);
  const restoreFocusIdRef = React.useRef(conversations.some(conversation => conversation.id === storedFocusedConversationId) ? storedFocusedConversationId : null);
  const [focusedConversationId, setFocusedConversationId] = React.useState(() => getInitialFocusedConversationId(conversations, activeConversationId, storedFocusedConversationId));
  const itemRefs = React.useRef(new Map());
  const conversationIds = React.useMemo(() => conversations.map(conversation => conversation.id), [conversations]);
  const ownerState = {
    conversationCount: conversations.length,
    activeConversationId,
    variant
  };
  const Scroller = slots?.scroller ?? ScrollRoot;
  const Viewport = slots?.viewport ?? ScrollViewport;
  const ListScrollbar = slots?.scrollbar ?? ScrollScrollbar;
  const ListScrollbarThumb = slots?.scrollbarThumb ?? ScrollThumb;
  const Root = slots?.root ?? 'div';
  const scrollerProps = useSlotProps({
    elementType: Scroller,
    externalSlotProps: slotProps?.scroller,
    ownerState,
    additionalProps: {
      style: clScrollRootStyle
    }
  });
  const viewportProps = useSlotProps({
    elementType: Viewport,
    externalSlotProps: slotProps?.viewport,
    ownerState,
    additionalProps: {
      style: clScrollViewportStyle
    }
  });
  const scrollbarProps = useSlotProps({
    elementType: ListScrollbar,
    externalSlotProps: slotProps?.scrollbar,
    ownerState,
    additionalProps: {
      orientation: 'vertical',
      style: scrollbarStyle
    }
  });
  const scrollbarThumbProps = useSlotProps({
    elementType: ListScrollbarThumb,
    externalSlotProps: slotProps?.scrollbarThumb,
    ownerState,
    additionalProps: {
      style: thumbStyle
    }
  });
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: {
      ref,
      role: 'listbox'
    }
  });
  const focusConversation = React.useCallback(id => {
    if (id == null) {
      return;
    }
    itemRefs.current.get(id)?.focus();
  }, []);
  React.useEffect(() => {
    if (focusedConversationId == null || conversationIds.includes(focusedConversationId)) {
      return;
    }
    setFocusedConversationId(getInitialFocusedConversationId(conversations, activeConversationId, storedFocusedConversationId));
  }, [activeConversationId, conversationIds, conversations, focusedConversationId, storedFocusedConversationId]);
  React.useEffect(() => {
    if (restoreFocusIdRef.current == null) {
      return;
    }
    focusConversation(restoreFocusIdRef.current);
    restoreFocusIdRef.current = null;
  }, [conversations, focusConversation]);
  const registerItemRef = React.useCallback((id, element) => {
    if (element == null) {
      itemRefs.current.delete(id);
      return;
    }
    itemRefs.current.set(id, element);
  }, []);
  const handleFocusedConversationChange = React.useCallback(id => {
    setFocusedConversationId(id);
    lastFocusedConversationIdByStore.set(store, id);
  }, [store]);
  const moveFocus = React.useCallback(targetIndex => {
    const boundedIndex = Math.max(0, Math.min(targetIndex, conversationIds.length - 1));
    const targetId = conversationIds[boundedIndex];
    if (targetId == null) {
      return;
    }
    handleFocusedConversationChange(targetId);
    focusConversation(targetId);
  }, [conversationIds, focusConversation, handleFocusedConversationChange]);
  const handleKeyDown = React.useCallback((event, id) => {
    const currentIndex = conversationIds.indexOf(id);
    if (currentIndex === -1) {
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(currentIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(currentIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        moveFocus(0);
        break;
      case 'End':
        event.preventDefault();
        moveFocus(conversationIds.length - 1);
        break;
      case 'Enter':
        event.preventDefault();
        void setActiveConversation(id);
        break;
      default:
        break;
    }
  }, [conversationIds, moveFocus, setActiveConversation]);
  const handleSelect = React.useCallback(id => {
    handleFocusedConversationChange(id);
    void setActiveConversation(id);
  }, [handleFocusedConversationChange, setActiveConversation]);
  return /*#__PURE__*/_jsxs(Scroller, _extends({}, scrollerProps, {
    children: [/*#__PURE__*/_jsx(Viewport, _extends({}, viewportProps, {
      children: /*#__PURE__*/_jsx(Root, _extends({}, rootProps, {
        children: conversations.map(conversation => {
          const unread = conversation.unreadCount != null && conversation.unreadCount > 0 || conversation.readState === 'unread';
          return /*#__PURE__*/_jsx(ConversationListRenderedItem, {
            conversation: conversation,
            focused: focusedConversationId === conversation.id,
            onFocus: handleFocusedConversationChange,
            onKeyDown: handleKeyDown,
            onSelect: handleSelect,
            registerItemRef: registerItemRef,
            selected: activeConversationId === conversation.id,
            slotProps: slotProps,
            slots: slots,
            unread: unread,
            variant: variant
          }, conversation.id);
        })
      }))
    })), /*#__PURE__*/_jsx(ListScrollbar, _extends({}, scrollbarProps, {
      children: /*#__PURE__*/_jsx(ListScrollbarThumb, _extends({}, scrollbarThumbProps))
    }))]
  }));
}), 'conversations');
if (process.env.NODE_ENV !== "production") ConversationListRoot.displayName = "ConversationListRoot";